/**
 * Print support shared by the in-app and public hypothetical pedigree views.
 *
 * "Save as PDF" here means the browser's own print dialog, so the watermark is a
 * print-only DOM overlay rather than anything baked into a generated file. It is
 * `display: none` on screen and only painted inside `@media print`.
 *
 * Two things are load-bearing for the watermark to actually appear in the PDF:
 *   - `print-color-adjust: exact` — browsers strip background/low-contrast color
 *     from print output by default, which would drop the watermark entirely.
 *   - `position: fixed` — in print context this repeats the layer on every page,
 *     so a pedigree that spills onto a second sheet stays watermarked.
 */
import React from 'react';
import { Box } from '@mui/material';

/** The mark is stacked, one array entry per line. */
export const DEFAULT_WATERMARK_LINES = ['KT Mountain Ranch', 'Quarter Horses'];

/** Single-line form, for headings rather than the mark itself. */
export const DEFAULT_WATERMARK_TEXT = DEFAULT_WATERMARK_LINES.join(' ');

export function PrintWatermark({ lines = DEFAULT_WATERMARK_LINES }) {
  return (
    <Box
      aria-hidden="true"
      className="pedigree-print-watermark"
      sx={{
        display: 'none',
        '@media print': {
          display: 'flex',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          // Stacked lines, centered as one block on the page.
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 9999,
          transform: 'rotate(-30deg)',
          color: 'rgba(0, 0, 0, 0.11)',
          fontSize: 'clamp(40px, 8vw, 88px)',
          fontWeight: 700,
          letterSpacing: '0.08em',
          lineHeight: 1.05,
          textAlign: 'center',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        },
      }}
    >
      {lines.map((line) => (
        <Box component="span" key={line}>
          {line}
        </Box>
      ))}
    </Box>
  );
}

/**
 * Print rules common to both pedigree views: strip the screen chrome, flatten
 * MUI cards to plain blocks, and keep the PedigreeTable's own cell colors, which
 * carry meaning (duplicated ancestors are color-coded).
 *
 * Anything the page wants hidden on paper gets className="no-print".
 */
export const pedigreePrintSx = {
  '@media print': {
    '& .no-print': { display: 'none !important' },
    // Content that exists only on paper — usually the text behind a control the
    // reader cannot operate there, such as a tooltip.
    '& .print-only': { display: 'block !important' },

    // Screen-sized headings cost the chart most of a page. The shared page leads
    // with an overline + h4 + subtitle, so it suffers worst; this keeps both
    // views' printed headers to roughly the same height.
    '& .MuiTypography-h4': {
      fontSize: '1.5rem !important',
      lineHeight: '1.2 !important',
      marginBottom: '2px !important',
    },
    '& .MuiTypography-subtitle1': { lineHeight: '1.3 !important' },
    '& .MuiTypography-overline': { lineHeight: '1.4 !important' },
    '& .MuiDivider-root': { margin: '8px 0 !important' },

    // Paper layout, which differs from the screen on purpose. On screen the
    // analysis is a narrow left column beside the chart; on paper that column
    // would waste most of a landscape sheet. So the two-column split collapses
    // and the page reads top-to-bottom:
    //
    //     title
    //     COI            linebreeding
    //     pedigree chart (full width)
    //
    // Collapsing the MUI grid gives each item the full width. Note this must be
    // `width`, not `flex-basis`: the grid container is turned into a flex COLUMN
    // below, where a basis of 100% would mean full HEIGHT and let the analysis
    // block eat the whole sheet, pushing the chart onto a second page.
    '& .MuiGrid-item': {
      width: '100% !important',
      maxWidth: '100% !important',
      flex: '0 0 auto !important',
    },
    // ...and the analysis stack, a column on screen, becomes three equal columns
    // so the COI sits under the title with linebreeding beside it. The two cards
    // fill columns one and two; the third is left empty as a spacer, which keeps
    // both cards a predictable third of the sheet regardless of how many
    // ancestors the linebreeding list happens to have.
    '& .pedigree-analysis': {
      display: 'grid !important',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '24px',
      alignItems: 'start',
      // MUI Stack spacing is a top margin on each child, which is meaningless
      // once they sit side by side.
      '& > :not(style) ~ :not(style)': { marginTop: '0 !important' },
      '& > *': { minWidth: 0 },
    },

    // Cards become plain blocks — borders and shadows waste toner and add
    // nothing on paper.
    '& .MuiCard-root': {
      border: 'none !important',
      boxShadow: 'none !important',
      breakInside: 'avoid',
    },
    // ...except the chart's own card. `break-inside: avoid` on something taller
    // than the space left on the page moves the WHOLE element to the next page,
    // which is what stranded the chart on page two under a half-empty page one.
    // The chart is the one thing here that may legitimately span a page break.
    '& .pedigree-chart .MuiCard-root': { breakInside: 'auto !important' },
    '& .MuiPaper-root': { boxShadow: 'none !important' },

    // Fill the sheet vertically instead of leaving the bottom half blank. The
    // page becomes one flex column and the chart takes whatever the header did
    // not use, so the chart grows or shrinks with the size of the analysis above
    // it. PedigreeTable's own `fillHeight` cannot do this — it measures with a
    // ResizeObserver at screen time and never re-runs for the print layout.
    display: 'flex',
    flexDirection: 'column',
    // A DEFINITE height, not min-height: `flex: 1` on the chart can only resolve
    // against a height that is already known, and min-height leaves it
    // indefinite, so the chart keeps its natural size and overflows to page two.
    height: '100vh',
    // The @page margin is 0 (see PedigreePrintStyles — it suppresses the
    // browser's URL footer), so the sheet's margin lives here instead. 100vh is
    // the full page box and box-sizing is border-box, so this padding is taken
    // out of the content area rather than added to it — no spill onto a second
    // sheet.
    padding: '12mm !important',

    '& .MuiGrid-container': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      // MUI sets flex-wrap: wrap. Left on, a height-constrained COLUMN flex
      // wraps its items into side-by-side COLUMNS instead of stacking them,
      // which puts the analysis and the chart back beside each other and throws
      // the chart onto its own page.
      flexWrap: 'nowrap',
      minHeight: 0,
      margin: '0 !important',
    },
    // The chart is the one item that grows, so it absorbs the leftover height.
    // Deliberately two classes deep: it has to outrank the .MuiGrid-item rule
    // above, and relying on declaration order between two equally specific
    // `!important` rules is not something to trust.
    //
    // No `break-inside: avoid` here — it would move the whole chart to a second
    // page the moment it did not quite fit, which is the blank-page failure this
    // is trying to avoid in the first place.
    '& .MuiGrid-item.pedigree-chart': {
      flex: '1 1 auto !important',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
    '& .pedigree-chart .MuiCard-root, & .pedigree-chart .MuiCardContent-root': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
    // Reclaim the card padding — on paper it is just a margin inside a margin.
    '& .pedigree-chart .MuiCardContent-root': { padding: '0 !important' },
    // The chart must be STRETCHED to fill the box reserved for it, not merely
    // given a box. PedigreeTable's wrapper sits at its natural height, so the
    // reserved space showed up as white space under a short chart. Making the
    // wrapper a flex column and letting the table grow inside it hands the slack
    // to the table, which spreads it evenly across the generation rows.
    // Two levels deep on purpose. The shared page nests CardContent > wrapper >
    // table, but the in-app page adds one more Box for its fullscreen mode:
    // CardContent > Box > wrapper > table. Stretching only the first level left
    // the table at its natural height there, short of the space reserved for it.
    // `:not(table)` keeps the table itself out of it — turning a table into a
    // flex container destroys its row layout; it is stretched by the rule below.
    [`& .pedigree-chart .MuiCardContent-root > *,
      & .pedigree-chart .MuiCardContent-root > * > *:not(table)`]: {
      flex: '1 1 auto',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    '& .pedigree-chart .pedigree-table': { flex: '1 1 auto' },

    // The chart must fit the sheet width rather than scroll.
    '& .pedigree-table': {
      width: '100% !important',
      minWidth: '0 !important',
      height: '100% !important',
      tableLayout: 'fixed',
    },
    '& table, & td, & th': {
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    },

    // Undo the on-screen fullscreen/scroll containers so nothing is clipped.
    '& *': { overflow: 'visible !important', maxHeight: 'none !important' },
  },
};

/**
 * Page-level print setup. Landscape gives the 6-column chart room to breathe.
 *
 * `hideAppChrome` emits global rules for the in-app view, where the sidebar and
 * header are rendered by Layout OUTSIDE this page's container and so cannot be
 * reached from a scoped `sx`. The public shared page has no chrome and leaves it
 * off.
 */
export function PedigreePrintStyles({ hideAppChrome = false }) {
  return (
    <style>{`
      @media print {
        /* margin: 0 is deliberate and load-bearing. The browser draws its own
           header/footer — date, title, page number and the PAGE URL — inside the
           @page margin area, and CSS cannot target them. With no margin there is
           nowhere to draw them, so they are suppressed. That matters here beyond
           tidiness: a shared pedigree's URL is an unguessable secret, and
           printing it onto the sheet hands it to anyone who sees the paper.
           The page's own breathing room moves to the container's padding. */
        @page { size: landscape; margin: 0; }
        /* Any stray margin here stacks on top of the page-filling container and
           spills a near-empty second sheet. */
        html, body, #root { background: #fff !important; margin: 0 !important; padding: 0 !important; }

        /* MUI renders tooltips, menus and dialogs through a PORTAL on
           document.body — outside this page's container, so container-scoped
           .no-print rules never reach them. A tooltip left open by the very
           click that started the print (hovering the print button opens one)
           then prints its text onto the sheet AND, being positioned at the
           button's on-screen x, extends the layout past the paper width, which
           makes Chrome shrink-to-fit the whole document. That is why a printed
           page could come out uniformly smaller than the same document printed
           from elsewhere. These must stay global, not scoped. */
        .MuiTooltip-popper,
        .MuiPopper-root,
        .MuiPopover-root,
        .MuiMenu-root,
        .MuiDialog-root,
        .MuiModal-root,
        .MuiBackdrop-root,
        .MuiSnackbar-root,
        .MuiAutocomplete-popper { display: none !important; }
        ${
          hideAppChrome
            ? `.MuiDrawer-root, .MuiAppBar-root, header, nav { display: none !important; }
               /* Layout gives main 24px of padding and a 100vh min-height, and
                  puts an empty <Toolbar/> spacer (~64px) above the outlet to
                  clear the fixed AppBar. On screen that is invisible; in print
                  it offsets the page-height container by ~88px and pushes its
                  last 88px onto a second, near-empty sheet. */
               main { margin: 0 !important; padding: 0 !important; width: 100% !important;
                      min-height: 0 !important; height: auto !important;
                      display: block !important; background: #fff !important; }
               main > .MuiToolbar-root { display: none !important; }`
            : ''
        }
      }
    `}</style>
  );
}
